import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  ChevronDown, 
  ChevronUp,
  Box,
  Filter,
  Gauge,
  Activity,
  Thermometer,
  Timer,
  Waves,
  Zap,
  Beaker,
  Droplets,
  RotateCw,
  Microscope
} from 'lucide-react';
import { ProcessFlowDiagram } from './ProcessFlowDiagram';
import { AssetGauge } from './AssetGauge';
import { Asset } from '../types';

export const ReverseOsmosisStage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    const initialAssets: Asset[] = [
      {
        id: 'hp-pump',
        name: 'High Pressure Pump',
        type: 'Multistage Pump',
        description: 'High-pressure feed pump for RO system',
        manufacturer: 'Grundfos',
        model: 'CR 155-3',
        installationDate: '2023-09-01',
        maintenanceSchedule: 'Quarterly',
        status: 'operational',
        metrics: [
          {
            name: 'Discharge Pressure',
            value: 15.5,
            unit: 'bar',
            normalRange: { min: 14, max: 16 },
            criticalRange: { min: 13, max: 17 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 15.5 + Math.random() * 0.4 - 0.2
            }))
          },
          {
            name: 'Flow Rate',
            value: 120,
            unit: 'm³/h',
            normalRange: { min: 100, max: 140 },
            criticalRange: { min: 90, max: 150 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 120 + Math.random() * 8 - 4
            }))
          },
          {
            name: 'Power Draw',
            value: 75,
            unit: 'kW',
            normalRange: { min: 65, max: 85 },
            criticalRange: { min: 60, max: 90 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 75 + Math.random() * 4 - 2
            }))
          }
        ]
      },
      {
        id: 'cartridge-filter',
        name: 'Cartridge Filter',
        type: 'Pre-filter System',
        description: '5-micron cartridge filter bank',
        manufacturer: 'Pentair',
        model: 'Big Blue 20"',
        installationDate: '2023-09-05',
        maintenanceSchedule: 'Monthly',
        status: 'warning',
        metrics: [
          {
            name: 'Differential Pressure',
            value: 0.8,
            unit: 'bar',
            normalRange: { min: 0.2, max: 0.8 },
            criticalRange: { min: 0.1, max: 1.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.8 + Math.random() * 0.1 - 0.05
            }))
          },
          {
            name: 'Flow Rate',
            value: 118,
            unit: 'm³/h',
            normalRange: { min: 100, max: 140 },
            criticalRange: { min: 90, max: 150 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 118 + Math.random() * 8 - 4
            }))
          }
        ]
      },
      {
        id: 'ro-train1',
        name: 'RO Train 1',
        type: 'Membrane System',
        description: 'Primary RO membrane array',
        manufacturer: 'DOW',
        model: 'FILMTEC™ BW30-400',
        installationDate: '2023-09-10',
        maintenanceSchedule: 'Semi-annual',
        status: 'operational',
        metrics: [
          {
            name: 'Feed Pressure',
            value: 15.2,
            unit: 'bar',
            normalRange: { min: 14, max: 16 },
            criticalRange: { min: 13, max: 17 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 15.2 + Math.random() * 0.4 - 0.2
            }))
          },
          {
            name: 'Permeate Flow',
            value: 85,
            unit: 'm³/h',
            normalRange: { min: 75, max: 95 },
            criticalRange: { min: 70, max: 100 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 85 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Recovery Rate',
            value: 75,
            unit: '%',
            normalRange: { min: 70, max: 80 },
            criticalRange: { min: 65, max: 85 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 75 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Salt Rejection',
            value: 99.5,
            unit: '%',
            normalRange: { min: 99, max: 99.8 },
            criticalRange: { min: 98.5, max: 100 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 99.5 + Math.random() * 0.2 - 0.1
            }))
          }
        ]
      },
      {
        id: 'energy-recovery',
        name: 'Energy Recovery',
        type: 'Pressure Exchanger',
        description: 'Energy recovery device',
        manufacturer: 'Energy Recovery Inc.',
        model: 'PX Q300',
        installationDate: '2023-09-15',
        maintenanceSchedule: 'Annual',
        status: 'operational',
        metrics: [
          {
            name: 'Efficiency',
            value: 96,
            unit: '%',
            normalRange: { min: 94, max: 98 },
            criticalRange: { min: 92, max: 99 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 96 + Math.random() * 0.8 - 0.4
            }))
          },
          {
            name: 'Differential Pressure',
            value: 0.5,
            unit: 'bar',
            normalRange: { min: 0.3, max: 0.7 },
            criticalRange: { min: 0.2, max: 0.8 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.5 + Math.random() * 0.08 - 0.04
            }))
          },
          {
            name: 'Boost Pressure',
            value: 1.2,
            unit: 'bar',
            normalRange: { min: 1.0, max: 1.4 },
            criticalRange: { min: 0.8, max: 1.6 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 1.2 + Math.random() * 0.08 - 0.04
            }))
          }
        ]
      },
      {
        id: 'antiscalant',
        name: 'Antiscalant Dosing',
        type: 'Chemical System',
        description: 'Scale inhibitor dosing system',
        manufacturer: 'ProMinent',
        model: 'Delta Optodrive',
        installationDate: '2023-09-20',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'Dosing Rate',
            value: 2.5,
            unit: 'L/h',
            normalRange: { min: 2.0, max: 3.0 },
            criticalRange: { min: 1.8, max: 3.2 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.5 + Math.random() * 0.2 - 0.1
            }))
          },
          {
            name: 'Tank Level',
            value: 65,
            unit: '%',
            normalRange: { min: 20, max: 90 },
            criticalRange: { min: 10, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 65 + Math.random() * 2 - 1
            }))
          }
        ]
      },
      {
        id: 'permeate-quality',
        name: 'Permeate Quality',
        type: 'Analyzer System',
        description: 'Water quality monitoring system',
        manufacturer: 'Hach',
        model: 'SC4500',
        installationDate: '2023-09-25',
        maintenanceSchedule: 'Weekly',
        status: 'operational',
        metrics: [
          {
            name: 'Conductivity',
            value: 12,
            unit: 'µS/cm',
            normalRange: { min: 5, max: 20 },
            criticalRange: { min: 0, max: 25 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 12 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'pH',
            value: 6.8,
            unit: 'pH',
            normalRange: { min: 6.5, max: 7.2 },
            criticalRange: { min: 6.0, max: 7.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 6.8 + Math.random() * 0.1 - 0.05
            }))
          },
          {
            name: 'TDS',
            value: 8,
            unit: 'mg/L',
            normalRange: { min: 5, max: 15 },
            criticalRange: { min: 0, max: 20 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 8 + Math.random() * 2 - 1
            }))
          }
        ]
      }
    ];

    setAssets(initialAssets);

    const interval = setInterval(() => {
      setAssets(prevAssets => prevAssets.map(asset => ({
        ...asset,
        metrics: asset.metrics.map(metric => {
          const newValue = metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02);
          return {
            ...metric,
            value: newValue,
            history: [
              ...metric.history.slice(1),
              {
                timestamp: Date.now(),
                value: newValue
              }
            ]
          };
        })
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getAssetIcon = (assetId: string) => {
    if (assetId.includes('pump')) return <Box className="h-6 w-6" />;
    if (assetId.includes('filter')) return <Filter className="h-6 w-6" />;
    if (assetId.includes('train')) return <Microscope className="h-6 w-6" />;
    if (assetId.includes('recovery')) return <RotateCw className="h-6 w-6" />;
    if (assetId.includes('antiscalant')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('quality')) return <Activity className="h-6 w-6" />;
    return <Activity className="h-6 w-6" />;
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Temperature')) return <Thermometer className="h-4 w-4" />;
    if (metricName.includes('Flow')) return <Waves className="h-4 w-4" />;
    if (metricName.includes('Time')) return <Timer className="h-4 w-4" />;
    if (metricName.includes('Power')) return <Zap className="h-4 w-4" />;
    if (metricName.includes('Level')) return <Activity className="h-4 w-4" />;
    if (metricName.includes('Pressure')) return <Gauge className="h-4 w-4" />;
    if (metricName.includes('pH') || metricName.includes('TDS') || metricName.includes('Conductivity')) return <Beaker className="h-4 w-4" />;
    if (metricName.includes('Efficiency') || metricName.includes('Recovery') || metricName.includes('Rejection')) return <Activity className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  const isMetricInRange = (value: number, metric: Asset['metrics'][0]) => {
    if (!metric.normalRange) return true;
    return value >= metric.normalRange.min && value <= metric.normalRange.max;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-blue-600 text-white flex items-center justify-between hover:bg-blue-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Filter className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Stage 5 - Reverse Osmosis</h2>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="mb-6">
            <ProcessFlowDiagram assets={assets} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            {assets.map(asset => 
              asset.metrics.slice(0, 1).map(metric => (
                <div key={`${asset.id}-${metric.name}`} className="bg-white rounded-lg shadow p-4">
                  <AssetGauge
                    value={metric.value}
                    min={metric.normalRange?.min || 0}
                    max={metric.normalRange?.max || 100}
                    unit={metric.unit}
                    title={`${asset.name} - ${metric.name}`}
                  />
                </div>
              ))
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <div
                key={asset.id}
                onClick={() => setSelectedAsset(asset.id === selectedAsset ? null : asset.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  asset.id === selectedAsset
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getAssetIcon(asset.id)}
                    <div>
                      <h3 className="font-medium">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {asset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between items-baseline">
                      <div className="flex items-center space-x-2">
                        {getMetricIcon(metric.name)}
                        <span className="text-sm text-gray-500">{metric.name}</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        isMetricInRange(metric.value, metric) 
                          ? 'text-gray-900' 
                          : 'text-red-600'
                      }`}>
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};